// Flag hands_on_board: a video's Board, the by-hand way to make everything. Three lanes of cards: the characters, the
// places and props, the scenes. Each card has its picture, name, state and the same actions the World, the Cast and
// the scene detail have (the same handlers and tools: nothing new on the server). Drop a picture file on a card to
// make it that card's reference (the upload sheet opens for this video with it, then the existing path attaches it:
// a place's picture, a scene's reference pictures; a character's page for "Start from a picture"), or drag a place's
// or a scene's picture onto another card. A "+" on each lane, and CommandBar (Ctrl/Cmd+K) runs the same actions.
import { Children, useEffect, useMemo, useRef, useState, type DragEvent as ReactDragEvent, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from "react";
import * as act from "../actions";
import { api } from "../api";
import { ASSET_DRAG, filterCommands, readDraggedAsset, sceneNext, sceneStatus, type CardStatus, type Command } from "../board";
import { characterPath, SHEET_CREDITS, STATE_WORD, versionThumb, type CharacterInfo } from "../characters";
import { director } from "../director";
import type { FormatInfo } from "../formats";
import { parseScope, type UploadWhat } from "../library";
import { addRef } from "../references";
import { Link, navigate } from "../router";
import { bump, toast } from "../store";
import type { Asset, Project } from "../types";
import { KIND_WORDS, scenesLine, type PlaceKind, type PlaceView } from "../world";
import { Icon, Sheet, Spinner, useLoad } from "./bits";
import { AddCharacter, castOf, SheetConfirm, startSheet, useCastRows, useSheetJobs } from "./Cast";
import { useModels } from "./ModelPicker";
import { MoreMenu } from "./MoreMenu";
import { PaidButton, usePrice } from "./PriceFirst";
import { LibraryPick } from "./References";
import { active, useJob } from "./ShotBoard";
import { UploadSheet } from "./Upload";
import { NewPlaceSheet, PlaceSheet, usePlaceJobs } from "./World";

export const BOARD_FLAG = "hands_on_board";

// ------------------------------------------------------------------ asking the Board for a sheet (the command bar)

export type BoardAsk = "character" | "location" | "prop" | "scene";
const BOARD_EVENT = "cao:board";
let pending: BoardAsk | null = null;
/** Opens one of the Board's "+" sheets; the Board takes it when it mounts if it isn't on screen yet. */
export function askBoard(what: BoardAsk) {
  pending = what;
  window.dispatchEvent(new CustomEvent(BOARD_EVENT));
}

// ------------------------------------------------------------------ the Board

type Target = { kind: "character"; id: string; name: string } | { kind: "place"; place: PlaceView } | { kind: "scene"; sceneId: string; n: number };
type Open =
  | { kind: "add-character" }
  | { kind: "new-place"; place: PlaceKind }
  | { kind: "new-scene" }
  | { kind: "place"; id: string }
  | { kind: "upload"; target: Target; files: File[] }
  | { kind: "library"; target: Target }
  | { kind: "sheet"; row: CharacterInfo };

const PRESET: Record<Target["kind"] | PlaceKind, UploadWhat> = { character: "person", place: "place", location: "place", prop: "prop", scene: "picture" };
const presetOf = (t: Target): UploadWhat => (t.kind === "place" ? PRESET[t.place.kind] : PRESET[t.kind]);
const targetName = (t: Target) => (t.kind === "scene" ? `scene ${t.n}` : t.kind === "place" ? t.place.name : t.name);

export function Board({ p, format, onProject, onOpenScene }: { p: Project; format: FormatInfo | null; onProject: (p: Project) => void; onOpenScene: (index: number) => void }) {
  const { data: places, error, reload } = useLoad(() => api.places(p.id), [p.id]);
  const drawing = usePlaceJobs(p.id, reload);
  const [open, setOpen] = useState<Open | null>(null);
  const models = useModels();
  const image = models?.find((m) => m.id === p.models.image) ?? models?.find((m) => m.step === "image" && m.isDefault);
  const sheetCredits = image?.credits != null ? image.credits * 3 : SHEET_CREDITS;
  const scenes = p.script?.scenes ?? [];
  const cast = castOf(p);
  const { rows } = useCastRows(p);
  const hosts = format?.voices === "hosts";

  // the command bar's "New …" (askBoard), now or when the Board mounts
  useEffect(() => {
    const take = () => {
      const w = pending;
      pending = null;
      if (w === "character") setOpen({ kind: "add-character" });
      else if (w === "location" || w === "prop") setOpen({ kind: "new-place", place: w });
      else if (w === "scene" && scenes.length) setOpen({ kind: "new-scene" });
    };
    take();
    window.addEventListener(BOARD_EVENT, take);
    return () => window.removeEventListener(BOARD_EVENT, take);
  }, [scenes.length]);

  /** Attach a picture (an uploaded one, a library one, one dragged from another card) as the card's reference. */
  const attach = async (t: Target, a: { id: string; name: string }) => {
    try {
      if (t.kind === "place") {
        // the World's "Use a library picture" (free)
        await api.addPlace(p.id, { kind: t.place.kind, name: t.place.name, assetId: a.id });
        director.context(`The user used the picture "${a.name}" for the ${KIND_WORDS[t.place.kind].one} "${t.place.name}" on the Board (free).`);
        toast(`${t.place.name} uses it now.`, "good");
        reload();
      } else if (t.kind === "scene") {
        // the scene's References "Add" (set_references): pinned, so the next frame sends it
        const view = await api.references(p.id, t.sceneId);
        const v = await api.setReferences(p.id, t.sceneId, addRef(view.overrides, `asset:${a.id}`));
        director.context(`The user added "${a.name}" to scene ${t.n}'s reference pictures on the Board. The next frame uses: ${v.picked.map((r) => r.label).join("; ") || "none"}.`);
        toast(`Scene ${t.n} uses it from its next frame.`, "good");
        bump("project");
      } else {
        // a character's sheet starts from a picture on its own page (price first there)
        toast(`"${a.name}" is in this video's files. Tap "Start from a picture" to make ${t.name}'s sheet from it.`, "good");
        navigate(characterPath({ projectId: p.id }, t.id));
      }
    } catch (e) {
      toast(`Couldn't use that picture. ${(e as Error).message}`, "bad");
    }
  };

  const drop = (t: Target) => ({
    files: (files: File[]) => setOpen({ kind: "upload", target: t, files }),
    asset: (a: { assetId: string; name: string }) => void attach(t, { id: a.assetId, name: a.name }),
  });

  const openPlace = open?.kind === "place" ? [...(places?.locations ?? []), ...(places?.props ?? [])].find((x) => x.id === open.id) : null;
  const placeList = [...(places?.locations ?? []), ...(places?.props ?? [])];

  return (
    <section className="board" aria-label="Board">
      <p className="board-lede">Everything in this video, by hand. Drop a picture on a card to use it there.</p>

      <Lane
        title={hosts ? "Hosts" : "Characters"}
        count={cast.length}
        add={
          <button type="button" className="icon-btn board-add" data-action="cast.add" aria-label={hosts ? "Add a host" : "Add a character"} onClick={() => setOpen({ kind: "add-character" })}>
            <Icon name="plus" size={18} />
          </button>
        }
        empty="No characters yet."
      >
        {cast.map((c) => (
          <CharacterCard key={c.id} p={p} id={c.id} name={c.name} refUrl={c.refUrl ?? null} row={rows.get(c.id.toLowerCase()) ?? null} sheetCredits={sheetCredits} onMake={(row) => setOpen({ kind: "sheet", row })} onLibrary={() => setOpen({ kind: "library", target: { kind: "character", id: c.id, name: c.name } })} drop={drop({ kind: "character", id: c.id, name: c.name })} />
        ))}
      </Lane>

      <Lane
        title="Places and props"
        count={places ? placeList.length : null}
        add={
          <MoreMenu
            label="New place or prop"
            trigger={<Icon name="plus" size={18} />}
            triggerClass="icon-btn board-add"
            items={(["location", "prop"] as PlaceKind[]).map((k) => ({ id: k, label: KIND_WORDS[k].New, hint: k === "location" ? "A room, a street, a landscape" : "A thing that comes back", icon: "image", dataAction: "world.new", onSelect: () => setOpen({ kind: "new-place", place: k }) }))}
          />
        }
        empty={error ? `Couldn't load them. ${error}` : places ? "No places or props yet." : undefined}
        loading={!places && !error}
      >
        {placeList.map((x) => (
          <PlaceCard key={`${x.kind}-${x.id}`} place={x} onOpen={() => setOpen({ kind: "place", id: x.id })} onLibrary={() => setOpen({ kind: "library", target: { kind: "place", place: x } })} drop={drop({ kind: "place", place: x })} />
        ))}
        {drawing.map((d) => (
          <li key={d.jobId} className="bcard is-busy" aria-live="polite">
            <span className="bcard-pic">
              <Spinner />
            </span>
            <span className="bcard-name">{d.name}</span>
            <span className="bcard-state bcard-busy">Drawing</span>
          </li>
        ))}
      </Lane>

      <Lane
        title="Scenes"
        count={scenes.length}
        add={
          scenes.length > 0 && (
            <button type="button" className="icon-btn board-add" data-action="scene.add" aria-label="Add a scene at the end" onClick={() => setOpen({ kind: "new-scene" })}>
              <Icon name="plus" size={18} />
            </button>
          )
        }
        empty="Write the script to see the scenes here."
      >
        {scenes.map((s, i) => (
          <SceneCard key={s.id} p={p} sceneId={s.id} n={i + 1} title={s.onScreenText || s.voiceover} onOpen={() => onOpenScene(i)} onLibrary={() => setOpen({ kind: "library", target: { kind: "scene", sceneId: s.id, n: i + 1 } })} drop={drop({ kind: "scene", sceneId: s.id, n: i + 1 })} />
        ))}
      </Lane>

      {open?.kind === "add-character" && <AddCharacter p={p} hosts={hosts} onClose={() => setOpen(null)} onProject={onProject} />}
      {open?.kind === "new-place" && places && (
        <NewPlaceSheet
          p={p}
          kind={open.place}
          prices={places.prices}
          onClose={() => setOpen(null)}
          onAdded={() => {
            setOpen(null);
            reload();
          }}
          onStarted={(d) => {
            setOpen(null);
            drawing.started(d);
          }}
        />
      )}
      {open?.kind === "place" && openPlace && places && <PlaceSheet p={p} place={openPlace} prices={places.prices} onClose={() => setOpen(null)} reload={reload} onProject={onProject} onStarted={drawing.started} />}
      {open?.kind === "new-scene" && <NewSceneSheet p={p} after={scenes.length} onClose={() => setOpen(null)} />}
      {open?.kind === "sheet" && (
        <SheetConfirm
          name={open.row.name}
          what="all"
          credits={sheetCredits}
          hasSheet={!!(open.row.approved || open.row.latest)}
          onClose={() => setOpen(null)}
          onConfirm={() => {
            const row = open.row;
            setOpen(null);
            void startSheet(row, { part: "all" }, sheetCredits);
          }}
        />
      )}
      {open?.kind === "upload" && (
        <UploadSheet
          scope={parseScope(`project:${p.id}`)}
          scopeName={p.title}
          preset={presetOf(open.target)}
          files={open.files}
          single
          onClose={() => setOpen(null)}
          onDone={(items) => {
            const t = open.target;
            const a = items.find((x) => x.kind === "image") ?? items[0];
            if (a) void attach(t, { id: a.id, name: a.name });
          }}
        />
      )}
      {open?.kind === "library" && (
        <LibraryPick
          p={p}
          title={`A picture for ${targetName(open.target)}`}
          exclude={[]}
          busy={false}
          onBack={() => setOpen(null)}
          onClose={() => setOpen(null)}
          onPick={(a: Asset) => {
            const t = open.target;
            setOpen(null);
            void attach(t, { id: a.id, name: a.name });
          }}
        />
      )}
    </section>
  );
}

function Lane({ title, count, add, empty, loading = false, children }: { title: string; count: number | null; add: ReactNode; empty?: string; loading?: boolean; children: ReactNode }) {
  const kids = Children.toArray(children);
  return (
    <section className="board-lane" aria-label={title}>
      <header className="board-lane-head">
        <h3>
          {title}
          {count !== null && count > 0 && <span className="board-count">{count}</span>}
        </h3>
        {add}
      </header>
      {loading ? <Spinner label="Loading" /> : kids.length ? <ul className="board-cards">{children}</ul> : empty ? <p className="muted board-empty">{empty}</p> : null}
    </section>
  );
}

// ------------------------------------------------------------------ cards

type Drop = { files: (f: File[]) => void; asset: (a: { assetId: string; name: string }) => void };

/** A card that takes a dropped picture file, or a picture dragged from another card. */
function useDropTarget(drop: Drop) {
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  const takes = (e: ReactDragEvent) => {
    const types = Array.from(e.dataTransfer?.types ?? []);
    return types.includes("Files") || types.includes(ASSET_DRAG);
  };
  return {
    over,
    props: {
      onDragEnter: (e: ReactDragEvent) => {
        if (!takes(e)) return;
        e.preventDefault();
        e.stopPropagation();
        depth.current++;
        setOver(true);
      },
      onDragOver: (e: ReactDragEvent) => {
        if (!takes(e)) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = "copy";
      },
      onDragLeave: () => {
        depth.current = Math.max(0, depth.current - 1);
        if (!depth.current) setOver(false);
      },
      onDrop: (e: ReactDragEvent) => {
        if (!takes(e)) return;
        e.preventDefault();
        e.stopPropagation();
        depth.current = 0;
        setOver(false);
        const a = readDraggedAsset(e.dataTransfer.getData(ASSET_DRAG));
        if (a) return drop.asset(a);
        const files = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith("image/"));
        if (files.length) drop.files(files.slice(0, 1));
        else toast("Drop a picture (PNG, JPEG or WebP).", "bad");
      },
    },
  };
}

const dragPicture = (assetId: string | null | undefined, name: string) =>
  assetId
    ? {
        draggable: true,
        onDragStart: (e: ReactDragEvent) => {
          e.dataTransfer.setData(ASSET_DRAG, JSON.stringify({ assetId, name }));
          e.dataTransfer.effectAllowed = "copy";
        },
      }
    : {};

function State({ s }: { s: CardStatus }) {
  return <span className={`bcard-state bcard-${s.tone}`}>{s.word}</span>;
}

function CharacterCard({ p, id, name, refUrl, row, sheetCredits, onMake, onLibrary, drop }: { p: Project; id: string; name: string; refUrl: string | null; row: CharacterInfo | null; sheetCredits: number; onMake: (row: CharacterInfo) => void; onLibrary: () => void; drop: Drop }) {
  const jobs = useSheetJobs(row?.id);
  const { over, props } = useDropTarget(drop);
  const making = jobs.length > 0 || row?.state === "making";
  const shown = row ? (row.state === "draft" || row.state === "making" ? row.latest : (row.approved ?? row.latest)) : null;
  const pic = versionThumb(shown) ?? refUrl;
  const state = making ? "making" : (row?.state ?? (refUrl ? "approved" : "none"));
  const href = characterPath({ projectId: p.id }, id);
  const tone = state === "approved" ? "done" : state === "draft" ? "draft" : state === "making" ? "busy" : "off";
  return (
    <li className={`bcard${over ? " is-over" : ""}`} {...props}>
      <Link to={href} className="bcard-pic" aria-label={`Open ${name}`}>
        {pic ? <img src={pic} alt="" loading="lazy" decoding="async" /> : <span className="bcard-initial">{name.slice(0, 1).toUpperCase()}</span>}
      </Link>
      <span className="bcard-name">{name}</span>
      <State s={{ word: STATE_WORD[state], tone }} />
      <span className="bcard-acts">
        {row && state === "none" && (
          <button type="button" className="btn btn-quiet btn-small" data-action="character.sheet" onClick={() => onMake(row)}>
            Make sheet · {sheetCredits}
          </button>
        )}
        {state === "draft" && (
          <Link to={href} className="btn btn-primary btn-small">
            Check it
          </Link>
        )}
        {(state === "approved" || (!row && state === "none")) && (
          <Link to={href} className="btn btn-quiet btn-small">
            Open
          </Link>
        )}
        <button type="button" className="icon-btn" aria-label={`Use a library picture for ${name}`} title="Use a picture" onClick={onLibrary}>
          <Icon name="image" size={16} />
        </button>
      </span>
    </li>
  );
}

function PlaceCard({ place, onOpen, onLibrary, drop }: { place: PlaceView; onOpen: () => void; onLibrary: () => void; drop: Drop }) {
  const { over, props } = useDropTarget(drop);
  const pic = place.thumbUrl || place.url;
  return (
    <li className={`bcard bcard-${place.kind}${over ? " is-over" : ""}`} {...props}>
      <button type="button" className="bcard-pic" onClick={onOpen} aria-label={`Open ${place.name}`} {...dragPicture(place.assetId, place.name)}>
        {pic ? <img src={pic} alt="" loading="lazy" decoding="async" draggable={false} /> : <Icon name="image" size={22} />}
      </button>
      <span className="bcard-name">{place.name}</span>
      <State s={{ word: `${place.kind === "prop" ? "Prop" : "Place"} · ${scenesLine(place.scenes)}`, tone: pic ? "done" : "off" }} />
      <span className="bcard-acts">
        <button type="button" className="btn btn-quiet btn-small" onClick={onOpen}>
          Open
        </button>
        <button type="button" className="icon-btn" data-action="world.replace" aria-label={`Use a library picture for ${place.name}`} title="Use a picture" onClick={onLibrary}>
          <Icon name="image" size={16} />
        </button>
      </span>
    </li>
  );
}

function SceneCard({ p, sceneId, n, title, onOpen, onLibrary, drop }: { p: Project; sceneId: string; n: number; title: string; onOpen: () => void; onLibrary: () => void; drop: Drop }) {
  const job = useJob(p.id, sceneId, ["frame", "clip"]);
  const { over, props } = useDropTarget(drop);
  const shot = p.shots[sceneId];
  const making = active(job);
  const status = sceneStatus(shot, making, job?.status === "failed");
  const next = sceneNext(shot, making);
  // PRICE-FIRST: the frame's own price on its button, only fetched for a scene that needs one
  const price = usePrice(p.id, next === "frame" ? "frame" : null, sceneId, [JSON.stringify(shot ?? null)]);
  const [busy, setBusy] = useState(false);
  const pic = shot?.frame?.url;
  return (
    <li className={`bcard bcard-scene${over ? " is-over" : ""}`} {...props}>
      <button type="button" className="bcard-pic" onClick={onOpen} aria-label={`Open scene ${n}`} {...dragPicture(shot?.frame?.assetId, `Scene ${n}`)}>
        {pic ? <img src={pic} alt="" loading="lazy" decoding="async" draggable={false} /> : <span className="bcard-initial">{n}</span>}
      </button>
      <span className="bcard-name">
        {n}. {title}
      </span>
      <State s={status} />
      <span className="bcard-acts">
        {next === "frame" ? (
          <PaidButton
            price={price}
            label="Make frame"
            dataAction="scene.frame"
            ask={{ title: `Make scene ${n}'s frame?`, lede: "A still picture for this scene, to approve before it's animated.", go: "Make it" }}
            run={(credits) => act.startStep(p.id, "frame", { sceneId }, credits)}
          />
        ) : next === "approve" ? (
          <button
            type="button"
            className="btn btn-primary btn-small"
            data-action="scene.approve"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await act.approve(p.id, sceneId, "frame");
              } finally {
                setBusy(false);
              }
            }}
          >
            Approve
          </button>
        ) : (
          <button type="button" className="btn btn-quiet btn-small" onClick={onOpen}>
            Open
          </button>
        )}
        <button type="button" className="icon-btn" data-action="refs.add" aria-label={`Add a reference picture to scene ${n}`} title="Use a picture" onClick={onLibrary}>
          <Icon name="image" size={16} />
        </button>
      </span>
    </li>
  );
}

/** The scene's "Add one after" (add_scene), at the end of the video. */
function NewSceneSheet({ p, after, onClose }: { p: Project; after: number; onClose: () => void }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Sheet title="New scene" onClose={onClose}>
      <form
        className="sheet-body"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!text.trim()) return;
          setBusy(true);
          const r = await act.tapTool("add_scene", { project_id: p.id, after, description: text.trim() }, `added a scene at the end of "${p.title}"`);
          setBusy(false);
          if (r) {
            bump("project");
            onClose();
          }
        }}
      >
        <label className="field">
          <span className="field-label">What does the new scene show?</span>
          <input value={text} onChange={(e) => setText(e.target.value)} maxLength={300} placeholder="Close-up of the mug, steam rising" data-autofocus />
          <span className="field-hint">It goes after scene {after}. Free; you can move it after.</span>
        </label>
        <button type="submit" className="btn btn-primary btn-block" data-action="scene.add" disabled={busy || !text.trim()}>
          {busy ? "Adding" : "Add it"}
        </button>
      </form>
    </Sheet>
  );
}

// ------------------------------------------------------------------ the command bar (Ctrl/Cmd+K)

export function CommandBar({ commands, onClose }: { commands: Command[]; onClose: () => void }) {
  const [q, setQ] = useState("");
  const [at, setAt] = useState(0);
  const shown = useMemo(() => filterCommands(commands.filter((c) => !c.disabled), q), [commands, q]);
  useEffect(() => setAt(0), [q]);
  const run = (c: Command | undefined) => {
    if (!c) return;
    onClose();
    // after the sheet has gone, so a sheet the command opens isn't closed with it
    window.setTimeout(() => c.run(), 0);
  };
  const key = (e: ReactKeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const d = e.key === "ArrowDown" ? 1 : -1;
      setAt((i) => (shown.length ? (i + d + shown.length) % shown.length : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      run(shown[at]);
    }
  };
  return (
    <Sheet title="Do something" onClose={onClose} className="cmdbar-sheet">
      <div className="sheet-body cmdbar">
        <label className="search cmdbar-search">
          <Icon name="search" size={18} />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={key} placeholder="New scene, Render, Add files…" aria-label="Type a command" role="combobox" aria-expanded aria-controls="cmdbar-list" aria-activedescendant={shown[at] ? `cmd-${shown[at]!.id}` : undefined} autoComplete="off" data-autofocus />
        </label>
        {shown.length ? (
          <ul className="cmdbar-list" id="cmdbar-list" role="listbox" aria-label="Commands">
            {shown.map((c, i) => (
              <li key={c.id} id={`cmd-${c.id}`} role="option" aria-selected={i === at}>
                <button type="button" className={`cmdbar-item${i === at ? " is-on" : ""}`} onMouseEnter={() => setAt(i)} onClick={() => run(c)}>
                  <span className="cmdbar-label">{c.label}</span>
                  {c.hint && <span className="cmdbar-hint">{c.hint}</span>}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">Nothing matches. Try fewer words.</p>
        )}
        <p className="cmdbar-tip muted">Ctrl K or ⌘ K opens this on any video.</p>
      </div>
    </Sheet>
  );
}
